/**
 * Consumers who sent an application before flow completion was recorded have never seen Your Plan.
 * On their first consumer page, this stamps `completedAt` once and opens Your Plan with the
 * "Your plan is ready" arrival. Only the consumer's own session stamps; advisors never do. The page
 * shows its loading state until the one attempt settles, so no step flashes on the way to Your Plan.
 * A failed save is over: the plan hook says so and the consumer stays where they are on the screen
 * they asked for, rather than waiting on a stamp that will never land.
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useIsConsumerSession } from '@/hooks/use-app-mode';
import { needsLegacyFlowCompletionStamp } from '../lib/flow-completion';
import { consumerStepPath } from '../lib/paths';
import { useConsumerPlan } from './use-consumer-plan';
import { PLAN_READY_STATE } from './use-plan-ready-arrival';

export function useLegacyFlowCompletionStamp(clientId: string) {
  const isConsumerSession = useIsConsumerSession();
  const navigate = useNavigate();
  const { plan, applications, isLoading, savePlan } = useConsumerPlan(clientId);
  const needsStamp =
    isConsumerSession && !isLoading && needsLegacyFlowCompletionStamp(plan, applications);
  // The one attempt this session makes. `done` covers a failed save too, so a plan that reverts to
  // unstamped never asks for the stamp again and never holds the page on its loading state.
  const [attempt, setAttempt] = useState<'idle' | 'saving' | 'done'>('idle');
  const hasStarted = useRef(false);

  useEffect(() => {
    if (!needsStamp || hasStarted.current) return;
    hasStarted.current = true;
    setAttempt('saving');
    savePlan({ completedAt: new Date().toISOString() })
      .then(() =>
        navigate(consumerStepPath(clientId, 'your-plan'), {
          replace: true,
          state: PLAN_READY_STATE,
        }),
      )
      .catch(() => undefined)
      .finally(() => setAttempt('done'));
  }, [clientId, navigate, needsStamp, savePlan]);

  return { isStamping: attempt === 'saving' || (attempt === 'idle' && needsStamp) };
}
